import { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router';
import { FeedbackDialog, FeedbackIcon } from './FeedbackDialog';
import styles from './FeedbackMenu.module.css';

/**
 * The header's "Geri bildirim" button: a small menu with "Yeni bildirim" (the dialog) and
 * "Bildirimlerim" (the page listing the user's reports, /feedback). Arrow keys move between the
 * two, Escape or a click elsewhere closes it. Off during "view as".
 */
export function FeedbackMenu({ disabled, disabledHint }: { disabled: boolean; disabledHint: string }) {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const [dialogOpen, setDialogOpen] = useState(false);
  const wrapRef = useRef<HTMLDivElement>(null);
  const buttonRef = useRef<HTMLButtonElement>(null);
  const itemsRef = useRef<(HTMLButtonElement | null)[]>([]);

  useEffect(() => {
    if (!open) return;
    itemsRef.current[0]?.focus();
    const outside = (e: PointerEvent) => {
      if (!wrapRef.current?.contains(e.target as Node)) setOpen(false);
    };
    document.addEventListener('pointerdown', outside);
    return () => document.removeEventListener('pointerdown', outside);
  }, [open]);

  function closeMenu() {
    setOpen(false);
    buttonRef.current?.focus();
  }

  function onMenuKey(e: React.KeyboardEvent) {
    const items = itemsRef.current.filter((x): x is HTMLButtonElement => !!x);
    const at = items.indexOf(document.activeElement as HTMLButtonElement);
    if (e.key === 'Escape') {
      e.preventDefault();
      closeMenu();
    } else if (e.key === 'ArrowDown' || e.key === 'ArrowUp') {
      e.preventDefault();
      const next = (at + (e.key === 'ArrowDown' ? 1 : items.length - 1)) % items.length;
      items[next]?.focus();
    } else if (e.key === 'Tab') {
      setOpen(false);
    }
  }

  const choose = (action: () => void) => () => {
    setOpen(false);
    action();
  };

  return (
    <div className={styles.wrap} ref={wrapRef}>
      <button
        ref={buttonRef}
        type="button"
        className={styles.button}
        aria-haspopup="menu"
        aria-expanded={open}
        aria-controls="feedback-menu"
        disabled={disabled}
        title={disabled ? disabledHint : t('feedback.open')}
        onClick={() => setOpen((o) => !o)}
        onKeyDown={(e) => {
          if (e.key === 'ArrowDown') {
            e.preventDefault();
            setOpen(true);
          }
        }}
        data-testid="feedback-button"
      >
        <FeedbackIcon />
        {t('nav.feedback')}
        <svg className={styles.caret} width={10} height={10} viewBox="0 0 10 10" aria-hidden="true" focusable="false">
          <path d="M2 3.5 5 6.5 8 3.5" fill="none" stroke="currentColor" strokeWidth={1.5} strokeLinecap="round" strokeLinejoin="round" />
        </svg>
      </button>
      {open && (
        <div id="feedback-menu" role="menu" aria-label={t('nav.feedback')} className={styles.menu} onKeyDown={onMenuKey}>
          <button
            ref={(el) => {
              itemsRef.current[0] = el;
            }}
            type="button"
            role="menuitem"
            className={styles.item}
            onClick={choose(() => setDialogOpen(true))}
          >
            <span className={styles.itemTitle}>{t('feedback.menuNew')}</span>
            <span className={styles.itemHint}>{t('feedback.menuNewHint')}</span>
          </button>
          <button
            ref={(el) => {
              itemsRef.current[1] = el;
            }}
            type="button"
            role="menuitem"
            className={styles.item}
            onClick={choose(() => navigate('/feedback'))}
          >
            <span className={styles.itemTitle}>{t('feedback.menuMine')}</span>
            <span className={styles.itemHint}>{t('feedback.menuMineHint')}</span>
          </button>
        </div>
      )}
      <FeedbackDialog open={dialogOpen} onClose={() => setDialogOpen(false)} />
    </div>
  );
}
